import React, { useState, useEffect, useCallback } from 'react';
import {
  Lightbulb, ThumbsUp, Send, Sparkles, ArrowLeft, ArrowRight,
} from 'lucide-react';
import { featureRequestsApi } from '../api/services';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { LinearProgressBar } from '@/components/ui/linear-progress';
import { PageHeader } from '@/components/ui/page-header';
import { cn } from '@/lib/utils';

interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  userStory?: string;
  acceptanceCriteria?: string;
  technicalNotes?: string;
  priority?: string;
  category?: string;
  status: string;
  adminNotes?: string;
  votes: number;
  submittedByName: string;
  submittedByEmail: string;
  createdAt: string;
  tenantId?: string;
}

// --- AI Story Builder helpers (client-side, no API call) ---

const detectCategory = (text: string): string => {
  const lower = text.toLowerCase();
  if (lower.includes('calendar')) return 'Calendar';
  if (lower.includes('work item') || lower.includes('task')) return 'WorkItems';
  if (lower.includes('report')) return 'Reports';
  if (lower.includes('dashboard')) return 'Dashboard';
  if (lower.includes('ea ') || lower.includes('assistant')) return 'EA';
  if (lower.includes('admin')) return 'Admin';
  return 'General';
};

const generateUserStory = (
  idea: string,
  who: string,
  frequency: string,
  importance: string,
) => {
  const role =
    who === 'Executive'
      ? 'an executive'
      : who === 'EA'
        ? 'an executive assistant'
        : 'an administrator';
  const benefit =
    frequency === 'Daily'
      ? 'save time every day'
      : frequency === 'Weekly'
        ? 'improve my weekly workflow'
        : 'have this capability when needed';

  return {
    userStory: `As ${role}, I want to ${idea.toLowerCase()}, so that I can ${benefit}.`,
    acceptanceCriteria:
      `- The feature should be accessible from the ${detectCategory(idea)} page\n` +
      `- It should work for all user roles that need it\n` +
      `- It should respect the current theme (light/dark)\n` +
      `- It should be responsive on mobile`,
    technicalNotes: `Category: ${detectCategory(idea)}\nFrequency: ${frequency}\nRequested by: ${who}`,
    priority:
      importance === 'Critical'
        ? 'High'
        : importance === 'Important'
          ? 'Medium'
          : 'Low',
    category: detectCategory(idea),
  };
};

// Status tone table — replaces the MUI colour string switch with explicit
// Tailwind classes that work across the shared palette.
const STATUS_TONE: Record<string, string> = {
  Submitted: 'border-border text-muted-foreground',
  UnderReview: 'border-sky-500 text-sky-600 dark:text-sky-400',
  Planned: 'border-primary text-primary',
  InProgress: 'border-amber-500 text-amber-600 dark:text-amber-400',
  Completed: 'border-emerald-500 text-emerald-600 dark:text-emerald-400',
  Declined: 'border-rose-500 text-rose-600 dark:text-rose-400',
};

const statusLabels: Record<string, string> = {
  Submitted: 'Submitted',
  UnderReview: 'Under Review',
  Planned: 'Planned',
  InProgress: 'In Progress',
  Completed: 'Completed',
  Declined: 'Declined',
};

const categories = ['All', 'Dashboard', 'Calendar', 'WorkItems', 'Reports', 'EA', 'Admin', 'General'];
const statuses = ['All', 'Submitted', 'UnderReview', 'Planned', 'InProgress', 'Completed', 'Declined'];

// --- Wizard ---

function HorizontalSteps({ activeStep, steps }: { activeStep: number; steps: string[] }) {
  return (
    <ol className="mb-4 flex items-center gap-2">
      {steps.map((step, i) => {
        const isActive = i === activeStep;
        const isDone = i < activeStep;
        return (
          <React.Fragment key={step}>
            <li className="flex flex-1 flex-col items-center gap-1 text-center">
              <span
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-full border text-xs font-semibold',
                  isActive
                    ? 'border-primary bg-primary text-primary-foreground'
                    : isDone
                      ? 'border-primary bg-primary/20 text-primary'
                      : 'border-border text-muted-foreground',
                )}
              >
                {i + 1}
              </span>
              <span className={cn('text-xs', isActive ? 'font-semibold' : 'text-muted-foreground')}>{step}</span>
            </li>
            {i < steps.length - 1 && (
              <div className={cn('h-px flex-1', isDone ? 'bg-primary' : 'bg-border')} />
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
}

function SubmitWizard({ onSubmitted }: { onSubmitted: () => void }) {
  const [step, setStep] = useState(0);
  const [idea, setIdea] = useState('');
  const [who, setWho] = useState('Executive');
  const [frequency, setFrequency] = useState('Weekly');
  const [importance, setImportance] = useState('Important');
  const [generated, setGenerated] = useState<ReturnType<typeof generateUserStory> | null>(null);
  const [title, setTitle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleGenerate = () => {
    const result = generateUserStory(idea, who, frequency, importance);
    setGenerated(result);
    const autoTitle = idea.length > 200 ? idea.slice(0, 197) + '...' : idea;
    setTitle(autoTitle.charAt(0).toUpperCase() + autoTitle.slice(1));
    setStep(3);
  };

  const handleSubmit = async () => {
    if (!generated || !title.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await featureRequestsApi.create({
        title: title.trim(),
        description: idea,
        userStory: generated.userStory,
        acceptanceCriteria: generated.acceptanceCriteria,
        technicalNotes: generated.technicalNotes,
        priority: generated.priority,
        category: generated.category,
      });
      setSuccess(true);
      setTimeout(() => { onSubmitted(); }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const stepLabels = ['Describe your idea', 'Clarifying questions', 'AI builds story', 'Review & submit'];

  return (
    <Card>
      <CardContent className="pt-6">
        <HorizontalSteps activeStep={step} steps={stepLabels} />

        {success && (
          <Alert variant="success" className="mb-3">
            <AlertDescription>Feature request submitted successfully! Redirecting to browse view...</AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="error" className="mb-3">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {step === 0 && (
          <div>
            <h3 className="mb-1 flex items-center gap-2 text-base font-semibold">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              What would you like Cadence to do?
            </h3>
            <p className="mb-3 text-sm text-muted-foreground">
              Describe your idea in plain language. Be as specific or general as you like.
            </p>
            <Textarea
              rows={4}
              placeholder="e.g. I'd like a way to see all my work items grouped by strategic driver on the dashboard..."
              value={idea}
              onChange={(e) => setIdea(e.target.value)}
              className="mb-3"
            />
            <div className="flex justify-end">
              <Button disabled={!idea.trim()} onClick={() => setStep(1)} className="gap-1.5">
                Next
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <h3 className="mb-1 flex items-center gap-2 text-base font-semibold">
              <Sparkles className="h-5 w-5 text-primary" />
              A few quick questions to shape the story
            </h3>
            <p className="mb-3 text-sm text-muted-foreground">
              These help us understand priority, audience, and scope.
            </p>

            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>Who would primarily use this feature?</Label>
                <Select value={who} onValueChange={setWho}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Executive">Executive</SelectItem>
                    <SelectItem value="EA">Executive Assistant</SelectItem>
                    <SelectItem value="Admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label>How often would you use this?</Label>
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Daily">Daily</SelectItem>
                    <SelectItem value="Weekly">Weekly</SelectItem>
                    <SelectItem value="Monthly">Monthly</SelectItem>
                    <SelectItem value="Rarely">Rarely</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label>How important is this to your workflow?</Label>
                <Select value={importance} onValueChange={setImportance}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Nice to have">Nice to have</SelectItem>
                    <SelectItem value="Important">Important</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-4 flex justify-between">
              <Button variant="ghost" onClick={() => setStep(0)} className="gap-1.5">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button onClick={handleGenerate} className="gap-1.5">
                Generate Story
                <Sparkles className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {step === 3 && generated && (
          <div>
            <h3 className="mb-1 flex items-center gap-2 text-base font-semibold">
              <Sparkles className="h-5 w-5 text-emerald-500" />
              Review your feature request
            </h3>
            <p className="mb-3 text-sm text-muted-foreground">
              Edit anything before submitting. The AI-generated story is a starting point.
            </p>

            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="fr-title">Title</Label>
                <Input
                  id="fr-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={200}
                />
                <p className="text-xs text-muted-foreground">{title.length}/200</p>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="fr-idea">Original Idea</Label>
                <Textarea
                  id="fr-idea"
                  rows={2}
                  value={idea}
                  onChange={(e) => setIdea(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="fr-story">User Story</Label>
                <Textarea
                  id="fr-story"
                  rows={2}
                  value={generated.userStory}
                  onChange={(e) => setGenerated({ ...generated, userStory: e.target.value })}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="fr-ac">Acceptance Criteria</Label>
                <Textarea
                  id="fr-ac"
                  rows={4}
                  value={generated.acceptanceCriteria}
                  onChange={(e) => setGenerated({ ...generated, acceptanceCriteria: e.target.value })}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="fr-tech">Technical Notes</Label>
                <Textarea
                  id="fr-tech"
                  rows={2}
                  value={generated.technicalNotes}
                  onChange={(e) => setGenerated({ ...generated, technicalNotes: e.target.value })}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="border-primary text-primary">Priority: {generated.priority}</Badge>
                <Badge variant="secondary">Category: {generated.category}</Badge>
              </div>
            </div>

            <div className="mt-4 flex justify-between">
              <Button variant="ghost" onClick={() => setStep(1)} className="gap-1.5">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || !title.trim() || success}
                className="gap-1.5"
              >
                <Send className="h-4 w-4" />
                {submitting ? 'Submitting...' : 'Submit Feature Request'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- Browse ---

function BrowseRequests() {
  const [requests, setRequests] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [votingId, setVotingId] = useState<string | null>(null);

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await featureRequestsApi.getAll();
      setRequests(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const handleVote = async (id: string) => {
    setVotingId(id);
    try {
      const result = await featureRequestsApi.vote(id);
      setRequests((prev) =>
        prev.map((r) => (r.id === id ? { ...r, votes: result.votes } : r)),
      );
    } catch {
      // vote failed silently
    } finally {
      setVotingId(null);
    }
  };

  const filtered = requests.filter((r) => {
    if (categoryFilter !== 'All' && r.category !== categoryFilter) return false;
    if (statusFilter !== 'All' && r.status !== statusFilter) return false;
    return true;
  });

  if (loading) {
    return <LinearProgressBar />;
  }

  return (
    <div>
      {error && (
        <Alert variant="error" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="mb-3 flex flex-wrap gap-2">
        <div className="flex flex-col gap-1" style={{ minWidth: 140 }}>
          <Label className="text-xs">Category</Label>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1" style={{ minWidth: 140 }}>
          <Label className="text-xs">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {statuses.map((s) => (
                <SelectItem key={s} value={s}>{statusLabels[s] || s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="py-6 text-center text-sm text-muted-foreground">
              No feature requests found. Be the first to submit one!
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((req) => (
            <Card key={req.id}>
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  {/* Vote */}
                  <div className="flex min-w-[60px] flex-col items-center">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleVote(req.id)}
                            disabled={votingId === req.id}
                            className="text-primary"
                          >
                            <ThumbsUp className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Vote for this feature</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <span className="text-lg font-bold text-primary">{req.votes}</span>
                  </div>

                  <div className="flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-1.5">
                      <span className="text-base font-semibold">{req.title}</span>
                      <Badge
                        variant="outline"
                        className={STATUS_TONE[req.status] || 'border-border text-muted-foreground'}
                      >
                        {statusLabels[req.status] || req.status}
                      </Badge>
                      {req.category && <Badge variant="outline">{req.category}</Badge>}
                      {req.priority && (
                        <Badge variant="outline" className="border-amber-500 text-amber-600 dark:text-amber-400">
                          {req.priority}
                        </Badge>
                      )}
                    </div>

                    <p className="mb-1 text-sm text-muted-foreground">{req.description}</p>

                    {req.userStory && (
                      <p className="mb-1 rounded-md bg-muted p-2 text-sm italic text-muted-foreground">
                        {req.userStory}
                      </p>
                    )}

                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>By {req.submittedByName}</span>
                      <span>{new Date(req.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Main Page ---

export default function FeatureRequestPage() {
  const [tab, setTab] = useState('browse');

  return (
    <div>
      <PageHeader
        icon={Lightbulb}
        iconClassName="text-amber-500"
        title="Feature Requests"
        subtitle="Submit ideas for Cadence and vote on community requests"
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="browse">Browse Requests</TabsTrigger>
          <TabsTrigger value="submit">Submit New Idea</TabsTrigger>
        </TabsList>
        <TabsContent value="browse">
          <BrowseRequests />
        </TabsContent>
        <TabsContent value="submit">
          <SubmitWizard onSubmitted={() => setTab('browse')} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
